<template>
  <container-component>
    <div>
      <sessions-header
        @show-autoreporting="data.showReportingModal = true"
        @click-download-report="data.showDownloadReportingModal = true"
      />

      <sessions-table
        v-model:offset="data.offset"
        :sessions="data.sessions"
        :loading="data.loading"
        :pagination="data.pagination"
        show-charger-column
        sticky-header-position="var(--header-filters-height)"
        @update:offset="methods.getSessions"
      />

      <sessions-download-report-modal
        v-if="data.showDownloadReportingModal && data.userSettings"
        v-model="data.userSettings.systemFormat"
        :sessions-count="data.pagination.total"
        @close="data.showDownloadReportingModal = false"
      />
      <sessions-reporting-modal
        v-if="data.showReportingModal && data.userSettings"
        v-model="data.userSettings.hasAutoReporting"
        @close="data.showReportingModal = false"
      />
    </div>
  </container-component>
</template>

<script setup lang="ts">
import state from '@/state'
import SessionsTable from '@/components/tables/SessionsTable.vue'
import ContainerComponent from '@/components/ContainerComponent.vue'
import SessionsReportingModal from '@/components/modals/SessionsReportingModal.vue'
import SessionsDownloadReportModal from '@/components/modals/SessionsDownloadReportModal.vue'
import SessionsHeader from '@/components/headers/SessionsHeader.vue'
import { trackDataEvent } from '@/engine/metrics/trackDataManager'
import { useI18n } from '@/hooks/useI18n.hook'
import { useToast } from '@wallbox/toolkit-ui'
import { reactive, watch } from 'vue'

import { AUTH_USE_CASES, injectStrict, SESSION_USE_CASES } from '@/engine/injectors'
import type { Session } from '@/core/session'
import type { ReportingSettings } from '@/core/auth'

const i18n = useI18n()
const toast = useToast()

const sessionUsesCases = injectStrict(SESSION_USE_CASES)
const authUseCases = injectStrict(AUTH_USE_CASES)

type DataType = {
  showDownloadReportingModal: boolean
  showReportingModal: boolean,
  sessions: Session[]
  offset: number,
  loading: boolean,
  userSettings?: ReportingSettings
  pagination: {
    limit: number,
    total: number,
    offset: number,
    multiPage: boolean
  }
}

const data: DataType = reactive({
  showDownloadReportingModal: false,
  showReportingModal: false,
  sessions: [],
  offset: 0,
  loading: true,
  pagination: {
    limit: 50,
    total: 0,
    offset: 0,
    multiPage: true
  }
})

const methods = {
  handleApplyFilters (filters: typeof state.filters.sessionsFilters) {
    trackDataEvent('apply-filter-sessions', { ...filters })
    data.offset = 0
    methods.getSessions()
  },

  async getSessions () {
    data.loading = true
    try {
      const result = await sessionUsesCases.getAllSessions(
        state.organizations.getCurrentOrganization.groupUid,
        {
          filters: state.filters.backendSessionsFilters,
          limit: data.pagination.limit,
          offset: data.offset,
          cache: 'network'
        }
      )
      data.sessions = result.data
      data.pagination.total = result.total
    } catch (e) {
      toast.error(i18n.t('mywb.error.unexpected-error'))
    } finally {
      data.loading = false
    }
  }
}

watch(() => state.filters.sessionsFilters, () => {
  methods.handleApplyFilters(state.filters.sessionsFilters)
}, { immediate: true, deep: true })

async function created () {
  data.userSettings = await authUseCases.getUserReportingSettings(state.global.getUserId)
}

created()
</script>
